import { Box, Grid, useTheme } from '@mui/material'
import {
  AccountDoc,
  DocType,
  EventDoc,
  EventStatus,
  GroupDoc,
  MasterLabelDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { Model } from 'types/model'

import ObserveService from 'services/db/Observe'
import { withObservables } from '@nozbe/watermelondb/react'
import { ComponentType, useState } from 'react'
import { isSomeDay } from 'utils/api/events'
import SelectItemWithSearchPopup, {
  SelectableItem
} from 'components/popups/SelectItemWithSearchPopup'
import { Circle } from '@mui/icons-material'
import {
  isLabelInsideMasterLabel,
  provideLabelName,
  provideTaskColor
} from 'utils/masterLabel'
import { EventPopupContent } from 'components/popups/eventInfo/EventPopup'
import { useAppUIType } from 'components/providers/AppUITypeProvider'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { CalendarRange } from 'types/components/calendar'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import i18n from 'i18n'
import Popup from 'components/popup/Popup'
import { ArrowRightGrey } from 'assets/img'
import { useEventPopoverCallbacks } from 'components/calendar/eventPopover/EventPopoverProvider'
import Logger from 'services/Logger'
import { isTrulyEmpty } from 'utils/object'

export interface SomedayItemProps {
  account: Model<AccountDoc>
  masterLabel: Model<MasterLabelDoc>
  userSettings: Model<UserSettingsDoc>
  events: Model<EventDoc>[]
  groups?: Model<GroupDoc>[]
  title?: string
  withArrow?: boolean
}

export function SomedayItem(props: SomedayItemProps) {
  const theme = useTheme()
  const masterLabelLabel = useAppUIType().selectedLabel
  const somedayEvents = props.events
    .filter((event) => isSomeDay(event))
    .filter((event) => {
      if (!masterLabelLabel) return true // return all items

      if (event.Label && typeof event.Label !== 'string') {
        return event.Label.some((ele) => ele.LabelText === masterLabelLabel?.ID)
      }
    })
  const [showEvents, setShowEventsList] = useState(
    LocalStorage.get(LocalStorageKey.VISIBLE_EVENT) === '1'
  )

  function toggleShowEventsList(b: boolean) {
    setShowEventsList(b)
  }

  const [openEventId, setOpenEventId] = useState<string | undefined>(undefined)

  const hasLabels = somedayEvents.some(
    (event) =>
      event.Label && typeof event.Label !== 'string' && event.Label.length > 0
  )

  let biggestWidth = 0

  for (let i = 0; i < somedayEvents.length; i++) {
    const somedayEvent = somedayEvents[i]
    const label = somedayEvent.Label

    if (typeof label !== 'string' && (label?.length ?? 0) > 0) {
      const firstLabel = provideLabelName(props.masterLabel, somedayEvent)

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context) {
        context.font = '18px Times'
        const metrics = context?.measureText(firstLabel)
        const width = metrics?.width ?? 0

        if (width > biggestWidth) biggestWidth = width
      }
    }
  }

  const { openPopover, closePopover } = useEventPopoverCallbacks()

  Logger.blue('Somedayitem render', {
    showEvents,
    openEventId
  })

  const somedayItem = (item: SelectableItem) => {
    const event = somedayEvents.find((event) => event._id === item.id)
    const isOpen = openEventId === event?._id

    if (!event) return undefined

    if (event) {
      const taskColor = provideTaskColor(
        props.masterLabel,
        event,
        props.account
      )

      return (
        <div
          key={event.id}
          onClick={(e) => {
            openPopover(
              event._id,
              <EventPopupContent
                event={event}
                userSettings={props.userSettings}
                onClose={function (
                  simpleClose?: boolean,
                  forceClose?: boolean
                ): void {
                  setOpenEventId(undefined)
                }}
                open={false}
                instanceStart={0}
                instanceEnd={0}
                account={props.account}
                groups={props.groups}
                fc_id={''}
                awayClickTriggered={false}
                setAwayClickTriggered={function (b: boolean): void {}}
                showSmallUI={false}
              />,
              false,
              undefined,
              e.currentTarget
            )
          }}
        >
          <div style={{ height: '100%', maxHeight: '40px', padding: 4 }}>
            <Grid
              item
              container
              onClick={(e) => {
                setOpenEventId(event._id)
              }}
              sx={{ cursor: 'pointer' }}
              display={'flex'}
              flexWrap={'nowrap'}
              flexDirection={'row'}
              gap={2}
              width={'100%'}
              justifyItems={'center'}
              alignItems={'center'}
            >
              <Circle
                style={{
                  width: 14,
                  height: 14,
                  color: taskColor
                }}
              />
              <div
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '14px',
                  maxWidth: hasLabels ? `${biggestWidth}px` : '0px',
                  minWidth: hasLabels ? `${biggestWidth}px` : '0px',
                  width: hasLabels ? `${biggestWidth}px` : '0px',
                  color: theme.palette.text.secondary
                }}
              >
                {provideLabelName(props.masterLabel, event)}
              </div>
              <div
                style={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  height: 'auto',
                  textDecoration:
                    event.Status === EventStatus.COMPLETED
                      ? 'line-through'
                      : 'none',
                  fontSize: '14px',
                  color: theme.palette.text.primary
                }}
              >
                {event?.Text}
              </div>
            </Grid>
          </div>
        </div>
      )
    }

    return undefined
  }

  return (
    <SelectItemWithSearchPopup
      containerId="someday-tasks-container"
      width="500px"
      closeOnTouchOutside={isTrulyEmpty(openEventId)}
      doNotReactOnAway
      showSearch={false}
      data={somedayEvents
        .sort((event1, event2) => {
          const label1 =
            typeof event1.Label === 'string'
              ? ''
              : event1.Label?.filter((label) =>
                  isLabelInsideMasterLabel(props.masterLabel, label.LabelText)
                )?.[0]?.LabelText
          const label2 =
            typeof event2.Label === 'string'
              ? ''
              : event2.Label?.filter((label) =>
                  isLabelInsideMasterLabel(props.masterLabel, label.LabelText)
                )?.[0]?.LabelText
          const len1 =
            typeof event1.Label === 'string' ? 0 : Number(label1 ?? 0)
          const len2 =
            typeof event2.Label === 'string' ? 0 : Number(label2 ?? 0)
          return len2 - len1
        })
        .map((event) => {
          const selectable: SelectableItem = {
            leftIcon: (
              <Circle
                style={{
                  color: provideTaskColor(
                    props.masterLabel,
                    event,
                    props.account
                  )
                }}
              />
            ),
            title: event.Text ?? '',
            id: event._id
          }

          return selectable
        })}
      open={showEvents || !isTrulyEmpty(openEventId)}
      setOpen={toggleShowEventsList}
      selectedItemId={undefined}
      rowBuilder={somedayItem}
      title={props.title}
    >
      <Box
        key={'someday-item'}
        onClick={(e) => {
          setShowEventsList(true)
        }}
        sx={{
          height: '100%',
          width: '100%',
          color: theme.palette.text.primary,
          marginRight:
            LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ===
            CalendarRange.LISTWEEK
              ? 3
              : 0,
          marginLeft:
            LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ===
            CalendarRange.LISTWEEK
              ? 3
              : 0,
          transform: 'translateY(-1px)',
          cursor: 'pointer'
        }}
      >
        <GroupcalTooltip
          placement="top"
          enterDelay={200}
          title={
            <Box
              display={'flex'}
              flexDirection={'column'}
              gap={'0.25rem'}
              alignItems={i18n.dir() === 'rtl' ? 'end' : 'start'}
            >
              {props.title}
            </Box>
          }
        >
          {props.title}
        </GroupcalTooltip>
        {props.withArrow && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: i18n.dir() === 'ltr' ? 0 : undefined,
              left: i18n.dir() === 'ltr' ? undefined : 0,
              backgroundColor: 'var(--color-surface-card)'
            }}
          >
            <ArrowRightGrey
              height={16}
              width={16}
              style={{
                marginTop: 2,
                transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)'
              }}
            />
          </div>
        )}
      </Box>
    </SelectItemWithSearchPopup>
  )
}

export default withObservables([], (props: SomedayItemProps) => ({
  account: ObserveService.Account(),
  masterLabel: ObserveService.MasterLabel(),
  userSettings: ObserveService.UserSettings(),
  events: ObserveService.Events(DocType.TASK),
  groups: ObserveService.Groups()
}))(SomedayItem as ComponentType<unknown>) as any
